import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, Typography } from '@mui/material';

import { InspectionForm } from './inspection.form';
import { inspectionColumns } from './inspection-grid-columns';
import { formSchema } from './form.schema';
import { SiTrackGrid } from '../../components/si-track-grid/si-track-grid';
import { SaveHookFormDialog } from '../../components/save-hook-form.dialog';
import { operationManagementApiKeyStore } from '../../api/masters/operation-management-api-key-store';
import { UserPermissionName } from '../../models/user-permission-name';
import { useNavigate } from 'react-router-dom';
import { useLoadData } from '../../hooks/useLoadData';
import { ApiResponse } from '../../models/api-response';
import { GridColumnConfig, GridPreference } from '../../models/gridCoumnConfig';
import { gridApiKeyStore } from '../../api/grid-api-key-store';
import { OperationManagement } from '../../models/operationManagement';
import { useSaveData } from '../../hooks/useSaveData';
import { State } from '@progress/kendo-data-query';
import useDebounce from '../../hooks/use-debounce';
import { SaveInspectionDialog } from './save-inspection.dialog';

interface managementArgs {
  fromDate?: string;
  toDate?: string;
  workOrderStatusID?: number;
}

interface InspectionComponent {
  managementArgs: managementArgs;
}

export const InspectionComponent = ({ managementArgs }: InspectionComponent) => {
  const [editItem, setEditItem] = useState<Partial<any>>();
  const navigate = useNavigate();
  const handleEditInspection = (inspection: any) => {
    navigate(`/workorders/edit/${inspection.workOrderID}`);
  };
  const handleEditClose = () => {
    setEditItem(undefined);
  };

  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout,
  } = useLoadData<ApiResponse<GridPreference>>(
    gridApiKeyStore.getGridLayout('OperationManagement', 'OperationManagement')
  );

  const { isLoading, data: OpeationManagements, mutateAsync: mutateInspectionAsync } = useSaveData<any>(
    operationManagementApiKeyStore.getOperationManagementInspections());

  useEffect(() => {
    if (managementArgs) {
      mutateInspectionAsync({ data: managementArgs });
    }
  }, [managementArgs])

  const handleUpdateOM = () => {
    mutateInspectionAsync({ data: managementArgs });
  }

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredOperationManagements, setFilteredOperationManagements] = useState<OperationManagement[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredOperationManagementList = OpeationManagements?.data?.filter((OperationManagement: any) =>
      Object.values(OperationManagement).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredOperationManagements(filteredOperationManagementList || []);
  }, [debouncedSearchTerm, OpeationManagements?.data]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'OperationManagement',
      pageName: 'OperationManagement',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'OperationManagement',
      pageName: 'OperationManagement',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  // ----------------------------------------------------------------------------------------

  return (
    <Card elevation={0}>
      <CardHeader
        className="subHead"
        title={
          <Typography variant="h3" color="textPrimary">
            INSPECTIONS
          </Typography>
        }
      />
      <CardContent className="formContainer">
        <SiTrackGrid
          fileTitle='operation_management_inspection'
          columns={inspectionColumns}
          dataItemKey="workOrderID"
          // queryConfig={operationManagementApiKeyStore.getInspections(managementArgs)}
          onEdit={handleEditInspection}
          actionPermissions={{
            edit: UserPermissionName.EDIT_OPERATIONMANAGEMENT,
            export: UserPermissionName.DOWNLOAD_GRID,
          }}
          isBusy={isLoading || isLoadingGridLayout}
          // -----------------Grid Layout-------
          dataItems={filteredOperationManagements || []}
          reorderable
          handleSearch={handleInputChange}
          handleSaveLayout={handleSaveLayout}
          isSaveLayout={true}
          gridConfigFromApi={gridConfig}
          handleResetLayout={handleResetLayout}
          reFixPageNumber
        // -----------------Grid Layout-------
        />
      </CardContent>
      {editItem && (
        <SaveInspectionDialog
          title={'UPDATE INSPECTION'}
          data={editItem}
          yupSchema={formSchema}
          onClose={handleEditClose}
          updateManagement={handleUpdateOM}
          size="xs"
        >
          <InspectionForm />
        </SaveInspectionDialog>
      )}
    </Card>
  );
};
